import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  CardHeader,
  Button,
  Alert,
} from "reactstrap";
import Select from "react-select";
import TableContainer from "../../components/Common/TableContainer";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Swal from "sweetalert2";
import { Download } from "lucide-react";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { exportSalaryAdvanceTransactionToExcel } from "../../components/Api/GmsSalaryAdvanceTransaction";
import axios from "axios";
import BASE_URL from "../../config";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

const SalaryAdvanceTransactionReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [businessList, setBusinessList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalBalance, setTotalBalance] = useState(0);
  const [autoDeductionAmount, setAutoDeductionAmount] = useState(0);
  const [hasAutoDeduction, setHasAutoDeduction] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [employeeBranch, setEmployeeBranch] = useState(null);
  const [employeeDepartment, setEmployeeDepartment] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSalaryAdvanceTransactionReport');
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    console.log('[SalaryAdvanceTransaction] Component mounted');
    console.log('[SalaryAdvanceTransaction] Has permission:', hasPermission);
    console.log('[SalaryAdvanceTransaction] Current transactions state:', transactions);
    if (hasPermission) {
      console.log('[SalaryAdvanceTransaction] Loading business list due to permission');
      loadBusinessList();
    } else {
      console.log('[SalaryAdvanceTransaction] No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businessList.length > 0 && !selectedBusiness) {
      const business = businessList.find(b => b.value === session.businessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businessList, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branchList.length > 0 && selectedBusiness?.value === session.businessId && !selectedBranch) {
      const branch = branchList.find(b => b.value === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branchList, session.branchId, selectedBusiness]);

  // Debug effect to log transactions state changes
  useEffect(() => {
    console.log('[SalaryAdvanceTransaction] Transactions state changed:', transactions);
    console.log('[SalaryAdvanceTransaction] Number of transactions:', transactions.length);
  }, [transactions]);

  const loadBusinessList = async () => {
    try {
      console.log('Loading businesses...');
      const response = await getBusinesses({});
      console.log('Business response:', response);
      const data = Array.isArray(response) ? response : [response];
      const businessOptions = data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_NameAr
      }));
      setBusinessList(businessOptions);
    } catch (error) {
      console.error('Error loading business list:', error);
    }
  };

  const loadBranchList = async (businessId) => {
    try {
      console.log('Loading branches for business:', businessId);
      const response = await getBranches({ Business_Id: businessId });
      console.log('Branch response:', response);
      const branchData = Array.isArray(response) ? response : [response];
      const branchOptions = branchData.map(b => ({
        value: b.branch_Id,
        label: b.branch_NameEn || b.branch_NameAr
      }));
      setBranchList(branchOptions);
    } catch (error) {
      console.error('Error loading branch list:', error);
    }
  };

  const loadDepartmentList = async (businessId) => {
    try {
      console.log('Loading departments for business:', businessId);
      const response = await getDepartments({ Business_Id: businessId });
      console.log('Department response:', response);
      const data = Array.isArray(response) ? response : [response];
      const departmentOptions = data.map(d => ({
        value: d.department_Id,
        label: d.department_NameEn || d.department_NameAr
      }));
      setDepartmentList(departmentOptions);
    } catch (error) {
      console.error('Error loading department list:', error);
    }
  };

  const loadEmployeeList = async (businessId, branchId = null) => {
    try {
      const filter = { employee_Business_Id: businessId };
      if (branchId) {
        filter.employee_Branch_Id = branchId;
      }
      // If user has session business/branch, use those if no selection
      if (!businessId && session.businessId) {
        filter.employee_Business_Id = session.businessId;
      }
      if (!branchId && session.branchId && session.businessId) {
        filter.employee_Branch_Id = session.branchId;
      }

      const response = await getEmployees(filter);
      // Handle both single object and array responses
      const employees = Array.isArray(response) ? response : [response];
      const employeeOptions = employees
        .filter(emp => emp && emp.employee_Id) // Ensure valid employee data
        .map(emp => ({
          value: emp.employee_Id,
          label: `${emp.employee_Code} - ${emp.employee_NameEn || emp.employee_NameAr}`
        }));
      setEmployeeList(employeeOptions);
    } catch (error) {
      console.error('Error loading employee list:', error);
    }
  };

  // Manual search function for employee search
  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBusiness) {
      console.log('Selected business changed:', selectedBusiness);
      const businessId = selectedBusiness.value;
      loadBranchList(businessId);
      loadDepartmentList(businessId);
      loadEmployeeList(businessId, selectedBranch?.value);
    } else if (session.businessId && businessList.length > 0) {
      // Auto-load if session has businessId
      const business = businessList.find(b => b.value === session.businessId);
      if (business) {
        loadBranchList(session.businessId);
        loadDepartmentList(session.businessId);
        loadEmployeeList(session.businessId, session.branchId);
      }
    }
  }, [selectedBusiness, selectedBranch]);

  const columns = React.useMemo(() => [
    {
      Header: t('S.No'),
      accessor: 'serial_number',
      id: 'serial_number',
      sortable: false,
      Cell: ({ row }) => row.index + 1
    },
    {
      Header: t('Transaction Date'),
      accessor: 'transaction_Date',
      id: 'transaction_Date',
      sortable: true,
      Cell: ({ value }) => value ? moment(value).format('DD/MM/YYYY') : '-'
    },
    {
      Header: t('Issued Amount'),
      accessor: 'issued_Amount',
      id: 'issued_Amount',
      sortable: true,
      Cell: ({ value }) => value ? Number(value).toFixed(2) : '0.00'
    },
    {
      Header: t('Recovered Amount'),
      accessor: 'recovered_Amount',
      id: 'recovered_Amount',
      sortable: true,
      Cell: ({ value }) => value ? Number(value).toFixed(2) : '0.00'
    },
    {
      Header: t('Balance Amount'),
      accessor: 'balance_Amount',
      id: 'balance_Amount',
      sortable: true,
      Cell: ({ value }) => value ? Number(value).toFixed(2) : '0.00'
    },
    {
      Header: t('Transaction Type'),
      accessor: 'transactionType',
      id: 'transactionType',
      sortable: true,
      Cell: ({ value }) => value || '-'
    }
  ], [t]);

  const handleExportExcel = async () => {
    if (!selectedBusiness || !selectedEmployee) {
      alert(t('Please select both Company and Employee'));
      return;
    }

    try {
      setIsLoading(true);
      const filterData = {
        business_Id: selectedBusiness.value,
        employee_Id: selectedEmployee.value,
        fromDate: fromDate || null,
        toDate: toDate || null,
        branch_Id: selectedBranch?.value || null,
        department_Id: selectedDepartment?.value || null
      };

      await exportSalaryAdvanceTransactionToExcel(filterData);
      Swal.fire("Success", "Salary advance transaction report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export salary advance transaction report", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    console.log('[SalaryAdvanceTransaction] handleApply called');
    console.log('[SalaryAdvanceTransaction] Selected Business:', selectedBusiness);
    console.log('[SalaryAdvanceTransaction] Selected Employee:', selectedEmployee);

    if (!selectedBusiness || !selectedEmployee) {
      console.log('[SalaryAdvanceTransaction] Validation failed - missing Business or Employee');
      alert(t('Please select both Company and Employee'));
      return;
    }

    setIsLoading(true);
    console.log('[SalaryAdvanceTransaction] Starting data fetch...');

    try {
      // Fetch employee details first
      console.log('[SalaryAdvanceTransaction] Fetching employee details for Employee_Id:', selectedEmployee.value);
      const employeeResponse = await axios.post(`${BASE_URL}/api/Employee/get`, {
        Employee_Id: selectedEmployee.value
      }, {
        headers: getAuthHeader()
      });
      console.log('[SalaryAdvanceTransaction] Employee response:', employeeResponse.data);

      const employeeData = Array.isArray(employeeResponse.data)
        ? employeeResponse.data[0]
        : employeeResponse.data;

      console.log('[SalaryAdvanceTransaction] Employee data:', employeeData);
      setEmployeeDetails(employeeData);

      // Fetch branch details using employee's branch ID
      if (employeeData?.employee_Branch_Id) {
        console.log('[SalaryAdvanceTransaction] Fetching branch details for Branch_Id:', employeeData.employee_Branch_Id);
        const branchResponse = await axios.post(`${BASE_URL}/api/Branch/get`, {
          Branch_Id: employeeData.employee_Branch_Id
        }, {
          headers: getAuthHeader()
        });

        const branchData = Array.isArray(branchResponse.data)
          ? branchResponse.data[0]
          : branchResponse.data;

        console.log('[SalaryAdvanceTransaction] Branch data:', branchData);
        setEmployeeBranch(branchData);
      }

      // Fetch department details using employee's department ID
      if (employeeData?.employee_Department_ID) {
        console.log('[SalaryAdvanceTransaction] Fetching department details for Department_Id:', employeeData.employee_Department_ID);
        const departmentResponse = await axios.post(`${BASE_URL}/api/Department/get`, {
          Department_Id: employeeData.employee_Department_ID
        }, {
          headers: getAuthHeader()
        });

        const departmentData = Array.isArray(departmentResponse.data)
          ? departmentResponse.data[0]
          : departmentResponse.data;

        console.log('[SalaryAdvanceTransaction] Department data:', departmentData);
        setEmployeeDepartment(departmentData);
      }

      // Fetch transactions
      const transactionFilter = {
        business_Id: selectedBusiness.value,
        employee_Id: selectedEmployee.value,
        fromDate: fromDate || null,
        toDate: toDate || null,
        branch_Id: selectedBranch?.value || null,
        department_Id: selectedDepartment?.value || null,
      };

      console.log('[SalaryAdvanceTransaction] Fetching transactions with filter:', transactionFilter);
      console.log('[SalaryAdvanceTransaction] API URL:', `${BASE_URL}/api/SalaryAdvanceTransaction/get`);

      const response = await axios.post(`${BASE_URL}/api/SalaryAdvanceTransaction/get`, transactionFilter, {
        headers: getAuthHeader()
      });

      console.log('[SalaryAdvanceTransaction] Transaction API response:', response);
      console.log('[SalaryAdvanceTransaction] Transaction response.data:', response.data);
      console.log('[SalaryAdvanceTransaction] Response data type:', Array.isArray(response.data) ? 'Array' : typeof response.data);
      console.log('[SalaryAdvanceTransaction] Response data length:', Array.isArray(response.data) ? response.data.length : 'N/A');

      const transactionData = Array.isArray(response.data) ? response.data : [];
      console.log('[SalaryAdvanceTransaction] Processed transaction data:', transactionData);
      console.log('[SalaryAdvanceTransaction] Number of transactions:', transactionData.length);

      setTransactions(transactionData);
      console.log('[SalaryAdvanceTransaction] Transactions state updated');

      // Calculate total balance from transactions
      console.log('[SalaryAdvanceTransaction] Calculating total balance from', transactionData.length, 'transactions');
      let balance = 0;
      transactionData.forEach((trans, idx) => {
        console.log(`[SalaryAdvanceTransaction] Transaction ${idx + 1}:`, {
          type: trans.transactionType,
          issued: trans.issued_Amount,
          recovered: trans.recovered_Amount,
          balance: trans.balance_Amount
        });
        if (trans.transactionType === 'SalaryAdvances') {
          balance += trans.issued_Amount || 0;
        } else if (trans.transactionType === 'PayrollDetails') {
          balance -= trans.recovered_Amount || 0;
        }
      });
      console.log('[SalaryAdvanceTransaction] Calculated total balance:', balance);
      setTotalBalance(balance);

      // Fetch salary advances to get auto deduction amount
      const advanceResponse = await axios.post(`${BASE_URL}/api/SalaryAdvance/get`, {
        Employee_Id: selectedEmployee.value
      }, {
        headers: getAuthHeader()
      });

      const advances = advanceResponse.data || [];

      // Calculate total monthly deduction from active auto-deduction advances
      const activeAdvances = advances.filter(adv =>
        adv.is_AutoDeduction === true &&
        adv.approved_Date &&
        adv.monthly_Deduction &&
        adv.monthly_Deduction > 0
      );

      const totalDeduction = activeAdvances.reduce((sum, adv) => sum + (adv.monthly_Deduction || 0), 0);

      // Set auto deduction amount as minimum of total balance and monthly deduction
      setAutoDeductionAmount(Math.min(balance, totalDeduction));

      // Check if any advance has auto deduction enabled
      const hasAutoDeduction = activeAdvances.length > 0;
      setHasAutoDeduction(hasAutoDeduction);

    } catch (error) {
      console.error('[SalaryAdvanceTransaction] Error loading data:', error);
      console.error('[SalaryAdvanceTransaction] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      setTransactions([]);
      setTotalBalance(0);
      setAutoDeductionAmount(0);
      setHasAutoDeduction(false);
      setEmployeeDetails(null);
      setEmployeeBranch(null);
      setEmployeeDepartment(null);
    } finally {
      setIsLoading(false);
      console.log('[SalaryAdvanceTransaction] Data fetch completed');
    }
  };

  if (!hasPermission) {
    return <div>{t('No permission to view this page')}</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title={t("Salary Advance Transaction Report")} pageTitle={t("Reports")} />

        <Card>
          <CardHeader className="d-flex align-items-center">
            <h4 className="card-title mb-0 flex-grow-1">{t("Salary Advance Transaction Report")}</h4>
            <Button
              color="success"
              onClick={handleExportExcel}
              disabled={!selectedBusiness || !selectedEmployee || isLoading}
              className="ms-2"
            >
              <Download className="me-1" size={16} />
              {t("Export Excel")}
            </Button>
          </CardHeader>
          <CardBody>
            <Row className="mb-4">
              <Col md={3}>
                <FormGroup>
                  <Label>{t("Company")} <span className="text-danger">*</span></Label>
                  <Select
                    value={selectedBusiness}
                    onChange={(value) => {
                      setSelectedBusiness(value);
                      setSelectedBranch(null);
                      setSelectedDepartment(null);
                      setSelectedEmployee(null);
                      setEmployeeSearchOptions([]);
                    }}
                    options={businessList}
                    placeholder={t("Select Company")}
                    className="select2-selection"
                    isClearable={!session.businessId}
                    isDisabled={!!session.businessId}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>{t("Branch")}</Label>
                  <Select
                    value={selectedBranch}
                    onChange={(value) => {
                      setSelectedBranch(value);
                      setSelectedDepartment(null);
                      setSelectedEmployee(null);
                      setEmployeeSearchOptions([]);
                      if (value && selectedBusiness) {
                        loadEmployeeList(selectedBusiness.value, value.value);
                      }
                    }}
                    options={branchList}
                    placeholder={t("Select Branch")}
                    isDisabled={!!session.branchId || !selectedBusiness}
                    className="select2-selection"
                    isClearable={!session.branchId}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>{t("Department")}</Label>
                  <Select
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                    options={departmentList}
                    placeholder={t("Select Department")}
                    isDisabled={!selectedBusiness}
                    className="select2-selection"
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>{t("Employee")} <span className="text-danger">*</span></Label>
                  <Select
                    value={selectedEmployee?.value ? employeeSearchOptions.find(opt => opt.value === selectedEmployee.value) || selectedEmployee : null}
                    onChange={(option) => {
                      setSelectedEmployee(option);
                      if (option) {
                        setEmployeeSearchOptions([]);
                      }
                    }}
                    options={employeeSearchOptions}
                    isLoading={employeeSearchLoading}
                    filterOption={() => true}
                    noOptionsMessage={({ inputValue }) => {
                      if (employeeSearchLoading) return "Searching...";
                      if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                      return "No employees found";
                    }}
                    loadingMessage={() => "Searching employees..."}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change' && selectedBusiness) {
                        if (searchTimeout) {
                          clearTimeout(searchTimeout);
                        }
                        const timeout = setTimeout(() => {
                          searchEmployeesManually(inputValue);
                        }, 300);
                        setSearchTimeout(timeout);
                      }
                      return inputValue;
                    }}
                    placeholder="Type at least 3 characters to search..."
                    isDisabled={!selectedBusiness}
                    className="select2-selection"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col md={3}>
                <FormGroup>
                  <Label>{t("From Date")}</Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="form-control"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>{t("To Date")}</Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="form-control"
                  />
                </FormGroup>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button
                  color="primary"
                  onClick={handleApply}
                  disabled={!selectedBusiness || !selectedEmployee || isLoading}
                >
                  {isLoading ? t("Loading...") : t("Apply")}
                </Button>
              </Col>
            </Row>

            {/* Updated Summary Section with employee details and auto deduction status */}
            {transactions.length > 0 && (
              <>
                <Row className="mb-3">
                  <Col xs={12}>
                    <div className="bg-light p-3 rounded">
                      <Row>
                        <Col md={3}>
                          <div className="text-muted small">Employee Code</div>
                          <div className="fw-medium">{employeeDetails?.employee_Code || selectedEmployee?.label?.split(' - ')[0]}</div>
                        </Col>
                        <Col md={3}>
                          <div className="text-muted small">Employee Name</div>
                          <div className="fw-medium">{employeeDetails?.employee_NameEn || selectedEmployee?.label?.split(' - ')[1]}</div>
                        </Col>
                        <Col md={2}>
                          <div className="text-muted small">Company</div>
                          <div className="fw-medium">{selectedBusiness?.label}</div>
                        </Col>
                        <Col md={2}>
                          <div className="text-muted small">Branch</div>
                          <div className="fw-medium">{employeeBranch?.branch_NameEn || '-'}</div>
                        </Col>
                        <Col md={2}>
                          <div className="text-muted small">Department</div>
                          <div className="fw-medium">{employeeDepartment?.department_NameEn || '-'}</div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col xs={12}>
                    <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded">
                      <div className="px-3 py-2 bg-white rounded shadow-sm" style={{ minWidth: '250px' }}>
                        <div className="text-muted mb-1">{t("Total Salary Advance Balance")}</div>
                        <div className="h5 mb-0 text-primary">{totalBalance.toFixed(2)}</div>
                      </div>
                      <div className="px-3 py-2 bg-white rounded shadow-sm text-center" style={{ minWidth: '200px' }}>
                        <div className="text-muted mb-1">{t("Auto Deduction")}</div>
                        <div className="h6 mb-0">
                          <span className={`badge ${hasAutoDeduction ? 'bg-success' : 'bg-secondary'}`}>
                            {hasAutoDeduction ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                      <div className="px-3 py-2 bg-white rounded shadow-sm" style={{ minWidth: '250px' }}>
                        <div className="text-muted mb-1">{t("Next Month's Auto Deduction Amount")}</div>
                        <div className="h5 mb-0 text-success">{autoDeductionAmount.toFixed(2)}</div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}

            <Row>
              <Col>
                <div className="table-responsive mt-4">
                  {transactions.length > 0 ? (
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>{t('S.No')}</th>
                          <th>{t('Transaction Date')}</th>
                          <th>{t('Issued Amount')}</th>
                          <th>{t('Recovered Amount')}</th>
                          <th>{t('Balance Amount')}</th>
                          <th>{t('Transaction Type')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((item, index) => (
                          <tr key={item.transaction_Id}>
                            <td>{index + 1}</td>
                            <td>{moment(item.transaction_Date).format("DD/MM/YYYY")}</td>
                            <td>{item.issued_Amount.toFixed(2)}</td>
                            <td>{item.recovered_Amount.toFixed(2)}</td>
                            <td>{item.balance_Amount.toFixed(2)}</td>
                            <td>{item.transactionType}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center my-3">
                      <p>{t("No transactions found")}</p>
                    </div>
                  )}
                  {isLoading && (
                    <div className="text-center my-3">
                      <p>{t("Loading...")}</p>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default SalaryAdvanceTransactionReport; 